import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { useParams } from "react-router-dom";

import { useAggregatedDataV2 } from "~/hooks/useAggregatedDataV2.ts";
import userViewGuard from "~/hooks/useViewPermssionsCheck";
import {
	centralInvoiceRecipientQueries,
} from "~/modules/billing/api/centralInvoiceRecipient/centralInvoiceRecipientQueries.ts";
import { useAllClients } from "~/modules/client/api/client/clientQueries";
import { useAllCompanies } from "~/modules/client/api/company/companyQueries.ts";
import CentralInvoiceRecipientDetailsView
	from "~/modules/client/components/centralInvoiceRecipient/CentralInvoiceRecipientDetailsView.tsx";
import LoadingPage from "~/pages/LoadingPage";
import { PermissionNames } from "~/types/entityNames.ts";

const CentralInvoiceRecipientDetailsPage: React.FC = () => {
	const guard = userViewGuard(PermissionNames.CanManageProjects);
	if (guard) return guard;

	const { clientId, centralInvoiceRecipientId } = useParams();
	if (undefined === clientId || undefined === centralInvoiceRecipientId) return <div>error</div>;

	const { data, options: { loadingPercent } } = useAggregatedDataV2({
		clientsCentralInvoiceRecipients: useQuery(centralInvoiceRecipientQueries.byClient(clientId)),
		clients: useAllClients(),
		companies: useAllCompanies(),

	});

	const { clientsCentralInvoiceRecipients, clients, companies } = data;

	const client = useMemo(() => {
		if (clients) {
			return clients.find(c => c.id === clientId);
		}
		return undefined;
	}, [clientId, clients]);

	const company = useMemo(() => {
		if (companies && client) {
			return companies.find(company => company.id === client.companyId);
		}
		return undefined;
	}, [companies, client]);

	const centralInvoiceRecipientData = useMemo(() => {
		if (clientsCentralInvoiceRecipients) {
			return clientsCentralInvoiceRecipients.find(c => c.id === centralInvoiceRecipientId);
		}
		return undefined;
	}, [centralInvoiceRecipientId, clientsCentralInvoiceRecipients]);

	if (loadingPercent < 100) return <LoadingPage pcent={loadingPercent} />;

	if(!centralInvoiceRecipientData || !client || !company) {
		return <div>error</div>;
	}

	return (
		<CentralInvoiceRecipientDetailsView
			centralInvoiceRecipient={centralInvoiceRecipientData}
			client={client}
			company={company}

		/>
	);
};

export default CentralInvoiceRecipientDetailsPage;
